import React, { Fragment, useContext, useState } from 'react';
import { useLocation, useMatch } from 'react-router-dom';
import BreadCrumb from '../../../components/common/BreadCrumb';
import commonArray from '../../../components/common/commonArray';
import Spinner from '../../../components/common/spinner';
import { GlobalContext, handleError } from '../../../context/Provider';
import henceforthApi from '../../../utils/henceforthApi';
import { Button } from '../../../components/ui/button';
import { Sparkles, Save, AlertCircle } from 'lucide-react';

const EditHighlight: React.FC = () => {
  const location = useLocation();
  const match = useMatch('product/:id/highlight/:highlight_id/edit');
  const { language_data, authState, toastMessage, authDispatch } = useContext(GlobalContext);

  const breadCrumbPath = [
    {
      name: `${language_data?.[authState.lang]?.common_home || 'Home'}`,
      url: `/`,
      active: '',
    },
    {
      name: `${language_data?.[authState.lang]?.common_product || 'Product'} ${language_data?.[authState.lang]?.common_list || 'List'
        }`,
      url: `/products/1`,
      active: '',
    },
    {
      name: `${language_data?.[authState.lang]?.common_product || 'Product'} ${language_data?.[authState.lang]?.common_deatil || 'Detail'
        }`,
      url: `/product/${match?.params.id}`,
      active: '',
    },
    {
      name: `${language_data?.[authState.lang]?.common_edit || 'Edit'} ${language_data?.[authState.lang]?.common_highlights || 'Highlight'
        }`,
      url: ``,
      active: 'not-allowed',
    },
  ];

  const newParam = new URLSearchParams(location.search);
  const [highlights, setHighlights] = useState<string>((newParam.get('content') as string) || '');
  const [loading, setLoading] = useState<boolean>(false);
  const [highlightsErr, setHighlightsErr] = useState<string>('');

  const edit = async () => {
    if (!highlights) {
      return setHighlightsErr(
        `${language_data?.[authState.lang]?.common_highlight || 'Highlight'} ${language_data?.[authState.lang]?.common_is || 'is'
        } ${language_data?.[authState.lang]?.common_required || 'required'}`
      );
    }
    if (!highlights.trim()) {
      return setHighlightsErr(
        `${language_data?.[authState.lang]?.common_please || 'Please'} ${language_data?.[authState.lang]?.common_write || 'write'
        } ${language_data?.[authState.lang]?.common_something || 'something'} ${language_data?.[authState.lang]?.common_in || 'in'
        } ${language_data?.[authState.lang]?.common_highlights || 'highlights'}`
      );
    }

    const items = {
      _id: match?.params.highlight_id,
      product_id: match?.params.id,
      content: highlights.trim(),
      language: 'ENGLISH',
    };

    setLoading(true);
    try {
      const apiRes = await henceforthApi.Product.editProductDetailsDynamically(
        commonArray.productOtherDetails[0],
        items
      );
      toastMessage(apiRes.message || 'Highlight updated successfully');
      window.history.back();
    } catch (error) {
      handleError(error, 'active', authDispatch);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setHighlights(e.target.value);
    setHighlightsErr('');
  };

  return (
    <Fragment>
      <BreadCrumb pathNameDeclare={breadCrumbPath} />

      <div className="w-full px-4 sm:px-6 lg:px-8 py-4 space-y-4">
        <div className="bg-white rounded-2xl border border-slate-200/80 shadow-xs p-4 sm:p-4 space-y-4">
          {/* Header */}
          <div className="flex items-center gap-3.5 pb-4 border-b border-slate-100">
            <div className="w-10 h-10 rounded-xl bg-sky-50 text-[#1AA7DE] flex items-center justify-center shrink-0 shadow-2xs">
              <Sparkles className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-bold text-slate-900 tracking-tight m-0">
                {language_data?.[authState.lang]?.common_edit || 'Edit'}{' '}
                {language_data?.[authState.lang]?.common_highlights || 'Product Highlight'}
              </h2>
              <p className="text-xs text-slate-500 font-medium m-0 mt-0.5">
                Update bullet point displayed on the buyer product detail page
              </p>
            </div>
          </div>

          {/* Form */}
          <form
            onSubmit={(e) => {
              e.preventDefault();
              edit();
            }}
            className="space-y-5"
          >
            <div className="space-y-1.5">
              <label className="block text-xs sm:text-sm font-bold text-slate-700 tracking-wide">
                {language_data?.[authState.lang]?.common_highlights || 'Highlight'}{' '}
                <span className="text-rose-500">*</span>
              </label>
              <input
                type="text"
                value={highlights}
                onChange={handleChange}
                placeholder={
                  language_data?.[authState.lang]?.common_enter_your
                    ? `${language_data?.[authState.lang]?.common_enter_your} ${language_data?.[authState.lang]?.common_highlights}`
                    : 'e.g. 100% Breathable Organic Cotton, Machine Washable'
                }
                className={`w-full px-4 py-2.5 rounded-xl border text-xs sm:text-sm font-medium transition-all focus:outline-none focus:ring-2 ${highlightsErr
                    ? 'border-rose-300 bg-rose-50/30 text-rose-900 focus:ring-rose-200 focus:border-rose-500'
                    : 'border-slate-200 bg-slate-50/50 hover:bg-white focus:bg-white text-slate-800 focus:ring-[#1AA7DE]/20 focus:border-[#1AA7DE]'
                  }`}
              />
              {highlightsErr && (
                <div className="flex items-center gap-1.5 text-xs font-semibold text-rose-600 pt-1">
                  <AlertCircle className="w-3.5 h-3.5 shrink-0" />
                  <span>{highlightsErr}</span>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center justify-end gap-2.5 pt-3 border-t border-slate-100">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => window.history.back()}
                disabled={loading}
              >
                {language_data?.[authState.lang]?.common_cancel || 'Cancel'}
              </Button>
              <Button
                type="submit"
                variant="default"
                size="sm"
                disabled={loading}
                className="gap-1.5"
              >
                {loading ? (
                  <Spinner color="text-white" />
                ) : (
                  <>
                    <Save className="w-4 h-4" />
                    <span>{language_data?.[authState.lang]?.common_edit || 'Update Highlight'}</span>
                  </>
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Fragment>
  );
};

export default EditHighlight;